"use client";

import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Compass, FileText, Target, ArrowRight, CircleCheck as CheckCircle2, MapPin, Globe, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
const pillars = [{
  icon: ShieldCheck,
  title: "Trust-building content over keyword stuffing",
  tag: "Authority & Credibility",
  description: "When a managing partner, corporate buyer, or private client searches for guidance, they evaluate your depth immediately. I focus on creating comprehensive answers to complex client problems rather than thin content engineered merely to capture clicks.",
  takeaway: "Pages structured to demonstrate clear, defensible domain competence."
}, {
  icon: FileText,
  title: "Thought leadership that pre-sells your advisory work",
  tag: "Editorial Strategy",
  description: "Your point of view is your competitive advantage. I help you translate proprietary methodologies, case rationales, and industry perspectives into discoverable resources that establish you as the definitive authority in your niche.",
  takeaway: "Articles and advisory guides written to withstand scrutiny by sophisticated peers."
}, {
  icon: Compass,
  title: "Balancing local reputation with national reach",
  tag: "Geographic Precision",
  description: "Most professional practices need both: prominent search visibility in their physical market or region, alongside authoritative national rankings for specialized practice areas. I calibrate technical architectures and entity signals to secure both fronts.",
  takeaway: "Optimised practice hubs that capture regional demand without limiting national mandates."
}, {
  icon: Target,
  title: "High-value intent over raw visit volume",
  tag: "Commercial Intent",
  description: "A law practice or consultancy does not need 50,000 accidental visitors looking for templates. I prioritize queries with commercial and advisory intent, ensuring your inquiries come from genuine prospects ready for an engagement.",
  takeaway: "Targeting refined buyer queries where one qualified lead justifies the investment."
}];
const contrasts = [{
  conventional: "Chasing maximum search volume and vanity rankings",
  myApproach: "Focusing on senior buyer queries that carry advisory intent"
}, {
  conventional: "Generic blog articles written for search engines first",
  myApproach: "Authentic thought leadership demonstrating verifiable technical knowledge"
}, {
  conventional: "Handoff to junior account coordinators or automated reports",
  myApproach: "Direct consultation with me at every stage of analysis and execution"
}, {
  conventional: "Isolated local citations or irrelevant directory links",
  myApproach: "Entity positioning, industry references, and clean technical foundations"
}];
export default function ProfessionalApproach() {
  return <section id="professionalservicesseoapproach" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-16 lg:mb-20">
          <Badge variant="outline" className="mb-4 text-xs font-semibold tracking-wide border-primary/20 text-primary bg-primary/5 px-3 py-1">
            Advisory & Professional Services
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-semibold text-foreground tracking-tight mb-5">
            How I approach SEO for professional services
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Consultancies, legal practices, and corporate advisors do not need thousands of casual visitors. You need qualified decision-makers who already respect your expertise before they book an initial call. Here is how I build that visibility.
          </p>
        </div>

        {/* Four Strategic Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-20">
          {pillars.map((pillar, index) => {
          const IconComponent = pillar.icon;
          return <motion.div key={pillar.title} data-index={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} className="h-full">
                <Card className="h-full border-border/80 bg-card hover:border-primary/40 transition-[border-color,box-shadow] duration-200 shadow-xs flex flex-col justify-between">
                  <CardHeader className="gap-3">
                    <div className="flex items-center justify-between">
                      <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <IconComponent className="size-5" />
                      </div>
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {pillar.tag}
                      </span>
                    </div>
                    <CardTitle className="text-xl font-default font-semibold text-foreground pt-2">
                      {pillar.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      {pillar.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="rounded-md bg-muted/60 p-3.5 border border-border/50 flex items-start gap-2.5">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <p className="text-xs sm:text-sm font-medium text-foreground">
                        {pillar.takeaway}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Direct Comparison: Traditional Agency vs. My Strategic Focus */}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-10 lg:p-12 mb-16 shadow-xs">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-xs font-medium text-primary mb-3">
                <Sparkles className="size-4" />
                <span>The distinction</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-default font-semibold text-foreground tracking-tight mb-4">
                Why generic SEO tactics fail specialized firms
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
                Most agencies apply retail or consumer playbooks to high-stakes advisory services. They celebrate raw impressions while partners sit through unqualified meetings. I design campaigns exclusively around practice depth and client value.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm text-foreground">
                  <div className="size-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <MapPin className="size-3.5" />
                  </div>
                  <span>Regional office strength without geographic restrictions</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-foreground">
                  <div className="size-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Globe className="size-3.5" />
                  </div>
                  <span>National practice footprint for specialised instructions</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 divide-y divide-border border border-border/80 rounded-xl bg-background overflow-hidden">
              <div className="grid grid-cols-2 p-3.5 bg-muted/50 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                <span>Standard Agency Formula</span>
                <span className="text-primary font-bold">My Personal Standard</span>
              </div>
              {contrasts.map((item, index) => <div key={index} data-index={index} className="grid grid-cols-1 sm:grid-cols-2 p-4 sm:p-5 gap-3 text-sm">
                  <div className="text-muted-foreground text-xs sm:text-sm pr-2 flex items-start gap-2">
                    <span className="text-muted-foreground/60 select-none">•</span>
                    <span>{item.conventional}</span>
                  </div>
                  <div className="text-foreground text-xs sm:text-sm font-medium flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{item.myApproach}</span>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* CTA Bar */}
        <div className="rounded-xl bg-muted/50 border border-border/70 p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h4 className="text-lg sm:text-xl font-default font-semibold text-foreground">
              Ready to evaluate your current organic visibility?
            </h4>
            <p className="text-sm text-muted-foreground">
              Speak directly with me about your practice goals and current search footprint.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/ContactPage">
                <span>Book a Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/B2BSeoLeadGenerationPage">
                <span>Explore B2B SEO</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}